// features/notificationSlider/notificationsSlice.ts
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getAuthContext } from '../../utils/jwt';
import { BASE_URL } from '../../utils/constants';

// Types based on API response
export interface NotificationEntity {
  entityType: string;
  entityId: string;
}

export interface Notification {
  notificationId: string;
  type: string;
  category: string;
  severity: string;
  title: string;
  message: string;
  entity: NotificationEntity;
  deeplink: string;
  read: boolean;
  createdAt: string;
}

interface NotificationsState {
  items: Notification[];
  unreadCount: number;
}

const initialState: NotificationsState = {
  items: [],
  unreadCount: 0,
};

// Async thunk to fetch notifications from API
export const fetchNotifications = createAsyncThunk(
  'notifications/fetchNotifications',
  async () => {
    const { token, tenantId, userId } = getAuthContext();

    if (!token || !tenantId) {
      throw new Error('Authentication context missing');
    }


    const response = await fetch(`${BASE_URL}/api/v1/notifications`, {
      method: 'GET',
      headers: {
        'X-Tenant-Id': tenantId,
        'X-User-Id': userId || '',
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch notifications: ${response.status}`);
    }

    const data: Notification[] = await response.json();
    return data;
  }
);

// Async thunk to fetch unread count from API
export const fetchUnreadCount = createAsyncThunk(
  'notifications/fetchUnreadCount',
  async () => {
    const { token, tenantId, userId } = getAuthContext();

    if (!token || !tenantId) {
      throw new Error('Authentication context missing');
    }


    const response = await fetch(`${BASE_URL}/api/v1/notifications/unread-count`, {
      method: 'GET',
      headers: {
        'X-Tenant-Id': tenantId,
        'X-User-Id': userId || '',
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch unread count: ${response.status}`);
    }

    const data: { unreadCount: number } = await response.json();
    return data.unreadCount;
  }
);

// Async thunk to delete a notification from API
export const deleteNotification = createAsyncThunk(
  'notifications/deleteNotification',
  async (notificationId: string, { rejectWithValue }) => {
    try {
      const { token, tenantId, userId } = getAuthContext();

      if (!token || !tenantId) {
        throw new Error('Authentication context missing');
      }


      const response = await fetch(`${BASE_URL}/api/v1/notifications/${notificationId}`, {
        method: 'DELETE',
        headers: {
          'X-Tenant-Id': tenantId,
          'X-User-Id': userId || '',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to delete notification: ${response.status}`);
      }

      return notificationId;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to delete notification');
    }
  }
);

// Async thunk to mark a notification as read
export const markNotificationAsRead = createAsyncThunk(
  'notifications/markAsRead',
  async (notificationId: string, { rejectWithValue }) => {
    try {
      const { token, tenantId, userId } = getAuthContext();

      if (!token || !tenantId) {
        throw new Error('Authentication context missing');
      }


      const response = await fetch(`${BASE_URL}/api/v1/notifications/${notificationId}/read`, {
        method: 'POST',
        headers: {
          'X-Tenant-Id': tenantId,
          'X-User-Id': userId || '',
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to mark notification as read: ${response.status}`);
      }

      return notificationId;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to mark notification as read');
    }
  }
);

// Async thunk to mark multiple notifications as read
export const markNotificationsAsBulkRead = createAsyncThunk(
  'notifications/markBulkAsRead',
  async (notificationIds: string[], { rejectWithValue }) => {
    try {
      const { token, tenantId, userId } = getAuthContext();

      if (!token || !tenantId) {
        throw new Error('Authentication context missing');
      }


      const response = await fetch(`${BASE_URL}/api/v1/notifications/read/bulk`, {
        method: 'POST',
        headers: {
          'X-Tenant-Id': tenantId,
          'X-User-Id': userId || '',
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ notificationIds }),
      });

      if (!response.ok) {
        throw new Error(`Failed to mark notifications as read: ${response.status}`);
      }

      return notificationIds;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to mark notifications as read');
    }
  }
);

// Async thunk to mark all notifications as read
export const markAllNotificationsAsRead = createAsyncThunk(
  'notifications/markAllAsRead',
  async (_, { rejectWithValue }) => {
    try {
      const { token, tenantId, userId } = getAuthContext();

      if (!token || !tenantId) {
        throw new Error('Authentication context missing');
      }


      const response = await fetch(`${BASE_URL}/api/v1/notifications/read/all?scope=all&recentDays=7`, {
        method: 'POST',
        headers: {
          'X-Tenant-Id': tenantId,
          'X-User-Id': userId || '',
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to mark all notifications as read: ${response.status}`);
      }

      return true;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to mark all notifications as read');
    }
  }
);

// Async thunk to bulk delete notifications
export const bulkDeleteNotifications = createAsyncThunk(
  'notifications/bulkDelete',
  async (notificationIds: string[], { rejectWithValue }) => {
    try {
      const { token, tenantId, userId } = getAuthContext();

      if (!token || !tenantId) {
        throw new Error('Authentication context missing');
      }


      const response = await fetch(`${BASE_URL}/api/v1/notifications/delete/bulk`, {
        method: 'POST',
        headers: {
          'X-Tenant-Id': tenantId,
          'X-User-Id': userId || '',
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ notificationIds }),
      });

      if (!response.ok) {
        throw new Error(`Failed to delete notifications: ${response.status}`);
      }

      return notificationIds;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to delete notifications');
    }
  }
);

const notificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.items = action.payload;
        // Update unread count from the fetched notifications
        state.unreadCount = action.payload.filter(n => !n.read).length;
      })
      .addCase(fetchUnreadCount.fulfilled, (state, action) => {
        state.unreadCount = action.payload;
      })
      .addCase(deleteNotification.fulfilled, (state, action) => {
        // Remove the deleted notification from the list
        state.items = state.items.filter(n => n.notificationId !== action.payload);
        // Update unread count
        state.unreadCount = state.items.filter(n => !n.read).length;
      })
      .addCase(markNotificationAsRead.fulfilled, (state, action) => {
        // Find and update the notification to mark as read
        const notification = state.items.find(n => n.notificationId === action.payload);
        if (notification) {
          notification.read = true;
        }
        // Update unread count
        state.unreadCount = state.items.filter(n => !n.read).length;
      })
      .addCase(markNotificationsAsBulkRead.fulfilled, (state, action) => {
        // Mark all selected notifications as read
        const notificationIds = action.payload;
        state.items = state.items.map(n =>
          notificationIds.includes(n.notificationId)
            ? { ...n, read: true }
            : n
        );
        // Update unread count
        state.unreadCount = state.items.filter(n => !n.read).length;
      })
      .addCase(markAllNotificationsAsRead.fulfilled, (state) => {
        // Mark all notifications as read
        state.items = state.items.map(n => ({ ...n, read: true }));
        // Update unread count to 0
        state.unreadCount = 0;
      })
      .addCase(bulkDeleteNotifications.fulfilled, (state, action) => {
        // Remove all deleted notifications from the list
        const notificationIds = action.payload;
        state.items = state.items.filter(n => !notificationIds.includes(n.notificationId));
        // Update unread count
        state.unreadCount = state.items.filter(n => !n.read).length;
      });
  },
});

export default notificationsSlice.reducer;